import { centsToFormattedDollars } from '@fruitful/fruitils';
import { MembershipBillingFrequency } from '@fruitful/types';
import {
    Elements,
    PaymentElement,
    useElements,
    useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import {
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    RowList,
    Spinner,
    YStack,
} from 'smoothie';

import { PremiumUpgradeDisclosure } from '../../banking/components/Disclosures';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { STRIPE_PUBLISHABLE_KEY } from '../../config/config';
import { formatPremiumPaymentTerms } from '../constants/premiumPricingCopy';
import {
    formatPaymentMethodLabel,
    useCreatePaymentMethodSetupIntent,
    useGetPaymentMethod,
} from '../hooks/usePaymentMethod';
import {
    STRIPE_CONFIRM_PARAMS,
    STRIPE_ELEMENTS_FONTS,
    STRIPE_PAYMENT_ELEMENT_FIELDS,
    useStripeElementsAppearance,
} from '../hooks/useStripeElementsAppearance';
import {
    DEFAULT_PREMIUM_BILLING_FREQUENCY,
    usePremiumFrequencyPricing,
    useUpgradeToPremium,
} from '../hooks/useUpgradeSubscription';
import BillingFrequencyToggle from './BillingFrequencyToggle';
import CreditCardPicker from './CreditCardPicker';
import PlanSummary, { PremiumHero } from './PlanSummary';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY ?? '');

interface UpgradeCheckoutProps {
    onSuccess: () => void;
}

const UpgradeCheckoutForm = ({ onSuccess }: UpgradeCheckoutProps) => {
    const user = useAuthedUser();
    const stripe = useStripe();
    const elements = useElements();
    const [isPaymentElementComplete, setIsPaymentElementComplete] =
        useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [billingFrequency, setBillingFrequency] =
        useState<MembershipBillingFrequency>(DEFAULT_PREMIUM_BILLING_FREQUENCY);
    const [useNewCard, setUseNewCard] = useState(false);
    const elementsAppearance = useStripeElementsAppearance({
        isDisabled: isSubmitting,
    });

    useEffect(() => {
        void elements?.update({ appearance: elementsAppearance });
    }, [elements, isSubmitting]);

    const { data: paymentMethod } = useGetPaymentMethod({ userId: user.id });
    const savedCard = paymentMethod?.brand != null ? paymentMethod : null;
    const shouldCollectNewCard = savedCard == null || useNewCard;

    const {
        pricing,
        isLoading: isPricingLoading,
        error: pricingError,
    } = usePremiumFrequencyPricing();
    const { mutateAsync: upgradeToPremium } = useUpgradeToPremium();

    useEffect(() => {
        if (pricingError != null) {
            setErrorMessage(pricingError);
        }
    }, [pricingError]);

    const handlePayNow = async () => {
        setErrorMessage(null);
        if (stripe == null || elements == null) {
            return;
        }

        setIsSubmitting(true);
        try {
            let paymentMethodId: string | undefined;
            if (shouldCollectNewCard) {
                const { setupIntent, error } = await stripe.confirmSetup({
                    elements,
                    confirmParams: STRIPE_CONFIRM_PARAMS,
                    redirect: 'if_required',
                });
                paymentMethodId =
                    typeof setupIntent?.payment_method === 'string'
                        ? setupIntent.payment_method
                        : setupIntent?.payment_method?.id;
                if (error != null || paymentMethodId == null) {
                    setErrorMessage(
                        error?.message ?? 'Could not confirm card details.',
                    );
                    return;
                }
            }

            // Attach (when a new card was collected), then switch now.
            // Policy prorates this upgrade: credits unused time and bills a
            // full new term — a free trial has nothing to credit. Omitting
            // paymentMethodId reuses whatever's already the member's default.
            await upgradeToPremium({
                body: { billingFrequency, paymentMethodId },
            });

            onSuccess();
        } catch (err) {
            setErrorMessage(
                err instanceof Error
                    ? err.message
                    : 'Something went wrong. Please try again.',
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const selectedPricing = pricing[billingFrequency];
    const dueTodayCents = selectedPricing?.stripePreview.amountDueNowCents;
    const dueTodayText =
        isPricingLoading || dueTodayCents == null
            ? '…'
            : centsToFormattedDollars(dueTodayCents);
    const annualCents =
        pricing[MembershipBillingFrequency.YEARLY]?.stripePreview
            .amountDueNowCents;
    const annualCommitmentText =
        annualCents == null
            ? '…'
            : formatPremiumPaymentTerms(
                  MembershipBillingFrequency.YEARLY,
                  annualCents,
              );
    const annualAmountText =
        annualCents == null ? '…' : centsToFormattedDollars(annualCents);

    return (
        <YStack gap={20} width="100%">
            <PremiumHero
                isLoading={isPricingLoading}
                annualCommitmentText={annualCommitmentText}
            />

            <BillingFrequencyToggle
                billingFrequency={billingFrequency}
                onChange={setBillingFrequency}
                pricing={pricing}
                disabled={isSubmitting}
            />

            <RowList width="100%" rowHeaderProps={{ title: 'Card' }}>
                {savedCard != null && (
                    <CreditCardPicker
                        savedCard={savedCard}
                        useNewCard={useNewCard}
                        disabled={isSubmitting}
                        onSelectSaved={() => {
                            setUseNewCard(false);
                        }}
                        onSelectNew={() => {
                            setUseNewCard(true);
                        }}
                    />
                )}
                {shouldCollectNewCard && (
                    <YStack
                        padding={16}
                        gap={8}
                        pointerEvents={isSubmitting ? 'none' : 'auto'}
                    >
                        <PaymentElement
                            options={{
                                layout: 'tabs',
                                fields: STRIPE_PAYMENT_ELEMENT_FIELDS,
                                readOnly: isSubmitting,
                            }}
                            onChange={(event) => {
                                setIsPaymentElementComplete(event.complete);
                            }}
                        />
                        {savedCard != null && (
                            <BodySmallRegular color="$gray1000">
                                This card will replace{' '}
                                {formatPaymentMethodLabel(savedCard)} as your
                                payment method.
                            </BodySmallRegular>
                        )}
                    </YStack>
                )}
            </RowList>

            <PlanSummary
                isLoading={isPricingLoading}
                annualCommitmentText={annualAmountText}
                dueTodayText={dueTodayText}
            />

            {errorMessage != null && (
                <BodySmallRegular color="$red1100">
                    {errorMessage}
                </BodySmallRegular>
            )}

            <PremiumUpgradeDisclosure />

            <Button
                text={isSubmitting ? 'Processing…' : 'Pay now'}
                variant={ButtonVariant.PRIMARY}
                size={ButtonSize.LARGE}
                disabled={
                    (shouldCollectNewCard && !isPaymentElementComplete) ||
                    isSubmitting ||
                    isPricingLoading ||
                    stripe == null
                }
                onPress={() => {
                    void handlePayNow();
                }}
            />
        </YStack>
    );
};

export const UpgradeCheckout = ({ onSuccess }: UpgradeCheckoutProps) => {
    const { mutateAsync: createSetupIntent } =
        useCreatePaymentMethodSetupIntent();
    const elementsAppearance = useStripeElementsAppearance();
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [initError, setInitError] = useState<string | null>(null);

    useEffect(() => {
        createSetupIntent({ body: {} })
            .then(({ clientSecret: secret }) => {
                setClientSecret(secret);
            })
            .catch((err: unknown) => {
                setInitError(
                    err instanceof Error
                        ? err.message
                        : 'Could not start checkout. Please try again.',
                );
            });
    }, []);

    if (initError != null) {
        return (
            <BodySmallRegular color="$red1100">{initError}</BodySmallRegular>
        );
    }

    if (clientSecret == null) {
        return <Spinner />;
    }

    return (
        <Elements
            stripe={stripePromise}
            options={{
                clientSecret,
                fonts: STRIPE_ELEMENTS_FONTS,
                appearance: elementsAppearance,
            }}
        >
            <UpgradeCheckoutForm onSuccess={onSuccess} />
        </Elements>
    );
};

export default UpgradeCheckout;
